import { jsx, jsxs } from 'react/jsx-runtime';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import Svg8BitBrowser from './assets/8bitBrowser.svg.js';
import { Flex } from '@fabric-app/ui/components/Flex';
import { styled } from 'styled-components';
import { IconSidebar24HomeIcon, IconDesktop16MiscSearch, IconDesktop16MiscCopy, IconDesktop16MiscDownload } from '@fabric-app/icons/svg';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { getResourceNamePlaceholder } from '../../../resources/utils/getResourceTitle.js';
import { cssLineClamp, mediaHover } from '@fabric-app/ui/constants/styled-utils';
import { ButtonNavigational } from '@fabric-app/ui/components/Button';
import { useQueryResourcePublishDetails } from '../../queries/useQueryResourcePublishDetails.js';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { useMutationPublishEnable } from '../../mutations/useMutationPublishEnable.js';
import { TextInput } from '@fabric-app/ui/components/TextInput/TextInput';
import Divider from '@fabric-app/ui/components/Divider';
import Switch from '@fabric-app/ui/components/Switch';
import { useMutationPublishUpdate } from '../../mutations/useMutationPublishUpdate.js';
import { useMutationPublishDisable } from '../../mutations/useMutationPublishDisable.js';
import useCopyToClipboard from '@fabric-app/hooks/useCopyToClipboard';
import { toast } from '../../../store/alerts.js';
import { useResourcePermissionsByCurrentUser } from '../../../resource-detail/components/context/getDisabledActionsReasons.js';
import { LinkExternal } from '@fabric-app/ui/components/Link/LinkExternal';
import { useMemo } from 'react';
import { useQueryResourceDetail } from '../../../resources/queries/useQueryResourceDetail.js';
import { Link } from '@fabric-app/compat';
import { appRoutes } from '../../../app/routing/routes.config.js';
import { generatePath } from '@fabric-app/utils/url/routing';
import { resourceSharingModal } from './resourceSharingStore.js';
import { Kind } from '@fabric/woody-client';

const ContentPublishResource = ({ resource }) => {
  const extension = resource.extension;
  const resourceName = resource.name || getResourceNamePlaceholder(resource);
  const { data: publishDetails, isLoading } = useQueryResourcePublishDetails(resource.id);
  const { mutate: publish, isPending: isPublishing } = useMutationPublishEnable();
  const { disabledActions } = useResourcePermissionsByCurrentUser(resource);
  return /* @__PURE__ */ jsx(ScrollArea.Default, {
    asFlex: true,
    innerDisplayFlex: true,
    children: /* @__PURE__ */ jsx(Modal.Body, {
      style: {
        paddingTop: 4
      },
      children: isLoading ? /* @__PURE__ */ jsx(Flex, {
        justifyContent: "center",
        alignItems: "center",
        flexGrow: true,
        style: {
          padding: "4rem 2rem"
        },
        children: /* @__PURE__ */ jsx(Spinner, {
          size: 24
        })
      }) : !publishDetails ? /* @__PURE__ */ jsxs(Flex, {
        direction: "column",
        gap: 24,
        justifyContent: "center",
        alignItems: "center",
        children: [
          /* @__PURE__ */ jsx(UnpublishedTitle, {
            children: disabledActions.publish || `Publish this ${resource.kind === Kind.FOLDER ? "space" : "file"} to the web`
          }),
          /* @__PURE__ */ jsx(ButtonNavigational, {
            disabled: !!disabledActions.publish,
            onClick: () => publish({
              resourceId: resource.id,
              resourceName,
              resourceKind: resource.kind
            }),
            isLoading: isPublishing,
            children: "Publish this"
          }),
          /* @__PURE__ */ jsxs(EmptyStateContainer, {
            children: [
              /* @__PURE__ */ jsx(Svg8BitBrowser, {}),
              /* @__PURE__ */ jsxs(EmptyStateContent, {
                children: [
                  /* @__PURE__ */ jsx(IconSidebar24HomeIcon, {
                    style: {
                      width: 20,
                      height: 20
                    }
                  }),
                  /* @__PURE__ */ jsxs(ItemTitle, {
                    children: [
                      resourceName,
                      extension && /* @__PURE__ */ jsxs(ItemExtension, {
                        children: [
                          ".",
                          extension
                        ]
                      })
                    ]
                  })
                ]
              })
            ]
          })
        ]
      }) : /* @__PURE__ */ jsx(PublishedContent, {
        canEditPublishingSettings: !disabledActions.publish,
        publishDetails,
        resource
      })
    })
  });
};
const ContentPublishAscendantPublished = ({ ascendantResourceId, resource }) => {
  const { data: publishedAscendantResource, isLoading } = useQueryResourceDetail(ascendantResourceId);
  const { data: publishDetails } = useQueryResourcePublishDetails(ascendantResourceId);
  const resourceId = resource.id;
  const resourceKind = resource.kind;
  const url = useMemo(() => {
    if (!publishDetails) return null;
    const url2 = new URL(publishDetails.url, window.location.origin);
    if (resourceKind === Kind.FOLDER && ascendantResourceId !== resourceId) {
      url2.searchParams.set("fid", resourceId);
    }
    if (resourceKind !== Kind.FOLDER) {
      url2.searchParams.set("rid", resource.parent?.id || "");
      url2.searchParams.set("expandedFdocId", resourceId);
    }
    return url2;
  }, [
    ascendantResourceId,
    resourceId,
    publishDetails,
    resourceKind,
    resource.parent?.id
  ]);
  return /* @__PURE__ */ jsx(ScrollArea.Default, {
    asFlex: true,
    innerDisplayFlex: true,
    children: /* @__PURE__ */ jsx(Modal.Body, {
      style: {
        paddingTop: 4
      },
      children: isLoading ? /* @__PURE__ */ jsx(Flex, {
        justifyContent: "center",
        alignItems: "center",
        flexGrow: true,
        style: {
          padding: "4rem 2rem"
        },
        children: /* @__PURE__ */ jsx(Spinner, {
          size: 24
        })
      }) : publishedAscendantResource ? /* @__PURE__ */ jsxs(Flex, {
        direction: "column",
        gap: 24,
        justifyContent: "center",
        alignItems: "center",
        children: [
          /* @__PURE__ */ jsxs(UnpublishedTitle, {
            children: [
              resource.kind === "folder" ? "Space" : "File",
              " published via",
              " ",
              /* @__PURE__ */ jsx(AscendingPublishedItemLink, {
                onClick: () => {
                  resourceSharingModal.open({
                    resourceId: publishedAscendantResource.id,
                    tab: "publish"
                  });
                },
                href: publishedAscendantResource.kind === "folder" ? generatePath(appRoutes.folder.detail, {
                  folderId: publishedAscendantResource.id
                }) : generatePath(appRoutes.space.detail, {
                  spaceId: publishedAscendantResource.id
                }),
                children: publishedAscendantResource.name
              })
            ]
          }),
          /* @__PURE__ */ jsx(ButtonNavigational, {
            as: LinkExternal,
            href: url?.toString() || "",
            children: "Visit URL"
          }),
          /* @__PURE__ */ jsxs(EmptyStateContainer, {
            children: [
              /* @__PURE__ */ jsx(Svg8BitBrowser, {}),
              /* @__PURE__ */ jsxs(EmptyStateContent, {
                children: [
                  /* @__PURE__ */ jsx(IconSidebar24HomeIcon, {
                    style: {
                      width: 20,
                      height: 20
                    }
                  }),
                  /* @__PURE__ */ jsxs(ItemTitle, {
                    children: [
                      resource.name,
                      resource.extension && /* @__PURE__ */ jsxs(ItemExtension, {
                        children: [
                          ".",
                          resource.extension
                        ]
                      })
                    ]
                  })
                ]
              })
            ]
          })
        ]
      }) : null
    })
  });
};
const ContentPublish = ({ resource }) => {
  const publishedAscendantResourceId = resource.publishedAncestors?.[0]?.resourceId;
  if (publishedAscendantResourceId) {
    return /* @__PURE__ */ jsx(ContentPublishAscendantPublished, {
      ascendantResourceId: publishedAscendantResourceId,
      resource
    });
  }
  return /* @__PURE__ */ jsx(ContentPublishResource, {
    resource
  });
};
const EmptyStateContainer = styled.div.withConfig({
  displayName: "ContentPublish__EmptyStateContainer",
  componentId: "sc-1082947d-0"
})([
  `position:relative;svg{width:100%;max-width:384px;}`
]);
const EmptyStateContent = styled.div.withConfig({
  displayName: "ContentPublish__EmptyStateContent",
  componentId: "sc-1082947d-1"
})([
  `position:absolute;top:30%;left:7.8%;right:7.8%;color:rgba(`,
  `,0.7);`
], cssVar["color-text-primary-rgb"]);
const ItemTitle = styled.div.withConfig({
  displayName: "ContentPublish__ItemTitle",
  componentId: "sc-1082947d-2"
})([
  `font-size:1rem;font-weight:600;margin-top:1.125rem;line-height:1.25rem;`,
  ``
], cssLineClamp(2));
const ItemExtension = styled.span.withConfig({
  displayName: "ContentPublish__ItemExtension",
  componentId: "sc-1082947d-3"
})([
  `opacity:0.7;`
]);
const UnpublishedTitle = styled.div.withConfig({
  displayName: "ContentPublish__UnpublishedTitle",
  componentId: "sc-1082947d-4"
})([
  `font-size:17px;font-weight:600;color:`,
  `;`
], cssVar["color-text-tertiary"]);
const ConfigItemTitle = styled.div.withConfig({
  displayName: "ContentPublish__ConfigItemTitle",
  componentId: "sc-1082947d-5"
})([
  `font-size:14px;font-weight:500;color:`,
  `;`
], cssVar["color-text-primary"]);
const ConfigItemIcon = styled.div.withConfig({
  displayName: "ContentPublish__ConfigItemIcon",
  componentId: "sc-1082947d-6"
})([
  `color:`,
  `;`
], cssVar["color-text-tertiary"]);
const ToggleItemConfig = ({ label, icon, enabled, onUpdate, disabled }) => {
  return /* @__PURE__ */ jsxs(Flex, {
    alignItems: "center",
    gap: 10,
    justifyContent: "space-between",
    children: [
      /* @__PURE__ */ jsxs(Flex, {
        alignItems: "center",
        gap: 10,
        children: [
          /* @__PURE__ */ jsx(ConfigItemIcon, {
            children: icon
          }),
          /* @__PURE__ */ jsx(ConfigItemTitle, {
            children: label
          })
        ]
      }),
      /* @__PURE__ */ jsx(Switch, {
        variant: "success",
        checked: enabled ?? false,
        onCheckedChange: (value) => onUpdate(value),
        disabled
      })
    ]
  });
};
const TOGGLE_ITEMS = [
  {
    label: "Allow indexing by search engines",
    property: "allowIndex",
    Icon: IconDesktop16MiscSearch
  },
  {
    label: "Allow visitors to copy to their Fabric",
    property: "allowCopy",
    Icon: IconDesktop16MiscCopy
  }
];
const DOWNLOAD_TOGGLE_ITEM = {
  label: "Allow visitors to download",
  property: "allowDownload",
  Icon: IconDesktop16MiscDownload
};
const PublishedContent = ({ publishDetails, resource, canEditPublishingSettings }) => {
  const resourceId = resource.id;
  const displayedItemUrl = publishDetails.url.replace("https://", "").replace("http://", "");
  const { mutate: updatePublish } = useMutationPublishUpdate();
  const { mutate: disablePublish } = useMutationPublishDisable();
  const [_, copy] = useCopyToClipboard();
  const toggleItems = useMemo(() => {
    return [
      ...TOGGLE_ITEMS,
      ...resource.fileUrl ? [
        DOWNLOAD_TOGGLE_ITEM
      ] : []
    ];
  }, [
    resource.fileUrl
  ]);
  return /* @__PURE__ */ jsxs(Flex, {
    direction: "column",
    gap: 24,
    justifyContent: "center",
    children: [
      /* @__PURE__ */ jsxs(Flex, {
        alignItems: "center",
        gap: 10,
        children: [
          /* @__PURE__ */ jsx(TextInput, {
            containerProps: {
              style: {
                flexGrow: 1
              }
            },
            variant: "secondary",
            readOnly: true,
            inputHeight: "lg",
            value: displayedItemUrl
          }),
          /* @__PURE__ */ jsx(ButtonNavigational, {
            onClick: () => {
              copy(publishDetails.url, {
                onSuccess: () => {
                  toast({
                    content: "Copied to clipboard"
                  });
                }
              });
            },
            children: "Copy"
          })
        ]
      }),
      toggleItems.map((item) => {
        return /* @__PURE__ */ jsx(ToggleItemConfig, {
          label: item.label,
          icon: /* @__PURE__ */ jsx(item.Icon, {}),
          enabled: publishDetails[item.property],
          onUpdate: (enabled) => updatePublish({
            resourceId,
            publishDetails: {
              [item.property]: enabled
            }
          }),
          disabled: !canEditPublishingSettings
        }, item.property);
      }),
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsxs(Flex, {
        justifyContent: "flex-end",
        gap: 12,
        children: [
          /* @__PURE__ */ jsx(ButtonNavigational, {
            variant: "bg-secondary",
            disabled: !canEditPublishingSettings,
            onClick: () => {
              disablePublish({
                resourceId
              });
            },
            children: "Unpublish"
          }),
          /* @__PURE__ */ jsx(ButtonNavigational, {
            as: LinkExternal,
            href: publishDetails.url,
            children: "Visit URL"
          })
        ]
      })
    ]
  });
};
const AscendingPublishedItemLink = styled(Link).withConfig({
  displayName: "ContentPublish__AscendingPublishedItemLink",
  componentId: "sc-1082947d-7"
})([
  `color:`,
  `;`,
  `{&:hover{opacity:0.7;}}`
], cssVar["color-app-primary-text"], mediaHover);

export { ContentPublish };
//# sourceMappingURL=ContentPublish.js.map
